import {
    API_POOL_ENDPOINT,
    API_POOL_TIMEOUT,
    API_POOL_INTERVAL,
    API_GET_ENDPOINTS,
    API_POST_ENDPOINTS, API_PUT_ENDPOINTS
} from "./ApiConfig"
import { BackendResponse } from "./apiInterfaces";
import {createContext, useContext} from "react";
import {UserStatus} from "../auth/AuthProvider";
import {Instructor} from "../model/instructor";
import {Scout} from "../model/scout";
import {Census} from "../model/census";
import {Unit} from "../model/unit";
import {OrganizationType} from "../utils/organizationType";
import {CensusForLeaderDTO, CensusByIdForLeaderDTO} from "../model/censusForLeaderDTO";
import {CensusComment} from "../model/censusComment";

export const ApiProviderContext = createContext<ApiProvider | undefined>(undefined);

export const useApiProvider = () => {
    const context = useContext(ApiProviderContext);

    if (!context) {
        throw new Error("useProvider context error");
    }
    return context;
};

export class ApiProvider {
    jwt: string | null = null;

    constructor(jwt: string | null = null) {
        this.jwt = jwt;
    }

    // GET
    getAccountStatus(): Promise<UserStatus> {return get(API_GET_ENDPOINTS.ACCOUNT_STATUS, this.jwt).then(object => mapStringToUserStatus(object)); };
    getID(): Promise<string> {return get(API_GET_ENDPOINTS.GET_ID, this.jwt); };
    async refreshAccount() {
        this.jwt = await get(API_GET_ENDPOINTS.REFRESH_ACCOUNT, this.jwt);
        sessionStorage.setItem('jwtToken', this.jwt);
        window.location.reload();
    };
    getInstructor(): Promise<Instructor> {return emptyGet(API_GET_ENDPOINTS.GET_INSTRUCTOR, this.jwt).then(requestId => this.pool(requestId))
        .then(data => data as Instructor); };
    getScout(): Promise<Scout> {return emptyGet(API_GET_ENDPOINTS.GET_SCOUT, this.jwt).then(requestId => this.pool(requestId))
        .then(scout => parseDatesForScout(scout as Scout)); };
    getCensus(): Promise<Census> {return emptyGet(API_GET_ENDPOINTS.GET_CENSUS, this.jwt).then(requestId => this.pool(requestId))
        .then(census => census as Census); };
    getLeaderCensuses(): Promise<CensusForLeaderDTO[]> {return this.getSortedCensusForLeaderFromPromise(emptyGet(API_GET_ENDPOINTS.GET_LEADER_CENSUSES, this.jwt)); };
    getLeaderCensus(id: string): Promise<CensusByIdForLeaderDTO> {return byIdGet(API_GET_ENDPOINTS.GET_CENSUS_FOR_LEADER, id, this.jwt)
        .then(requestId => this.pool(requestId)).then(census => {
            console.log(census);
            return census as CensusByIdForLeaderDTO;
        })};
        
    getScoutDistricts(): Promise<Unit[]> {return this.getSortedUnitsFromPromise(get(API_GET_ENDPOINTS.GET_SCOUT_DISTRICTS, this.jwt)); };
    getScoutRegions(organizationType: OrganizationType): Promise<Unit[]> {return this.getSortedUnitsFromPromise(byOrganizationGet(API_GET_ENDPOINTS.GET_SCOUT_REGIONS, organizationType, this.jwt)); };
    
    // POST
    signUp(payload: object): Promise<Instructor> {return post(API_POST_ENDPOINTS.SIGN_UP, payload, this.jwt).then(requestId => this.pool(requestId))
        .then(instructor => instructor as Instructor); };
    postScout(payload: object): Promise<Scout> {return post(API_POST_ENDPOINTS.POST_SCOUT, payload, this.jwt).then(requestId => this.pool(requestId))
        .then(scout => parseDatesForScout(scout as Scout)); };
    postCensus(payload: object): Promise<Census> {return post(API_POST_ENDPOINTS.POST_CENSUS, payload, this.jwt).then(requestId => this.pool(requestId))
        .then(census => census as Census); };
    
    // PUT
    updateScout(payload: object): Promise<Scout> {return put(API_PUT_ENDPOINTS.UPDATE_SCOUT, payload, this.jwt).then(requestId => this.pool(requestId))
        .then(scout => parseDatesForScout(scout as Scout)); };
    updateCensusState(payload: object): Promise<Census> {return put(API_PUT_ENDPOINTS.UPDATE_CENSUS_STATE, payload, this.jwt).then(requestId => this.pool(requestId))
        .then(census => census as Census); };
    getComments(): Promise<CensusComment[]> {return this.getSortedComments(put(API_PUT_ENDPOINTS.UPDATE_COMMENTS_READ, null, this.jwt)); };
    
    // OTHER
    googleLogin(code: string): Promise<string> {return googleLogin(API_GET_ENDPOINTS.LOGIN_GOOGLE, code); };
    
    // UTILS
    private pool(requestId: string): Promise<object> {return pool(requestId, this.jwt); };

    private getSortedUnitsFromPromise(promise: Promise<string>) : Promise<Unit[]> {
        return promise.then(requestId => this.pool(requestId))
            .then(data => {
                let d = data as { Units: Unit[] };
                let c = d.Units;
                c.sort((a, b) => b.Name!.localeCompare(a.Name!));
                return c;
            });
    }

    private getSortedCensusesFromPromise(promise: Promise<string>) : Promise<Census[]> {
        return promise.then(requestId => this.pool(requestId))
            .then(data => {
                let d = data as { Censuses: Census[] };
                let c = d.Censuses;
                c.sort((a, b) => b.CreateDate!.localeCompare(a.CreateDate!));
                return c;
            });
    }

    private getSortedCensusForLeaderFromPromise(promise: Promise<string>) : Promise<CensusForLeaderDTO[]> {
        return promise.then(requestId => this.pool(requestId))
            .then(data => {
                let d = data as { Censuses: CensusForLeaderDTO[] };
                let c = d.Censuses;
                c.sort((a, b) => b.CreateDate!.localeCompare(a.CreateDate!));
                return c;
            });
    }
    
    private getSortedComments(promise: Promise<string>) : Promise<CensusComment[]> {
        return promise.then(requestId => this.pool(requestId))
            .then(data => {
                let d = data as { Comments: CensusComment[] };
                let c = d.Comments;
                c.sort((a, b) => b.CreateDate!.localeCompare(a.CreateDate!));
                return c;
            })
    }
}

const mapStringToUserStatus = (status: string): UserStatus => {
    switch (status.toLowerCase()) {
        case 'login':
            return UserStatus.LoggedIn;
        case 'register':
            return UserStatus.ToRegister;
        case 'migrate':
            return UserStatus.ToMigrate;
        default:
            return UserStatus.None;
    }
};

const parseDatesForScout = (s: Scout) => {
    s.PwdDate = (s.PwdDate === "0001-01-01T00:00:00") ? '' : s.PwdDate.split('T')[0];
    s.PhmDate = (s.PhmDate === "0001-01-01T00:00:00") ? '' : s.PhmDate?.split('T')[0];
    s.HmDate = (s.HmDate === "0001-01-01T00:00:00") ? '' : s.HmDate?.split('T')[0];
    return s;
}

class GetParams {
    constructor(public name: string, public value: string | number) {}
}

const emptyGet = (endpoint: string, jwt: string | null = null) : Promise<string> =>
    getByParameters(endpoint, [], jwt);

const byIdGet = (endpoint: string, id: string, jwt: string | null = null) : Promise<string> => {
    return (id == 'undefined' || id == 'null') ? new Promise( async ( resolve, reject ) => {
        reject("Błąd wysyłania żądania");
    }) : getByParameters(endpoint, [new GetParams('id', id)], jwt);
}

const byOrganizationGet = (endpoint: string, organizationType: OrganizationType, jwt: string | null = null) : Promise<string> => getByParameters(endpoint, [new GetParams('organization', organizationType)], jwt)

const getByParameters = (endpoint: string, parameters: Array<GetParams>, jwt: string | null = null) : Promise<string> => {
    return new Promise( async ( resolve, reject ) => {
        console.log(endpoint);
        if (jwt == null || jwt == '') {
            reject("Błąd wysyłania żądania");
            return;
        }
        
        var params = parameters
            .map(item => `${item.name}=${item.value}`)
            .join('&');

        if(params.length>0) {
            params= "?"+params;
        }

        const response = await fetch(`${endpoint}${params}`, {
            method: 'GET',
            headers: {
                "Authorization": "Bearer  " + jwt,
                'Content-Type': 'application/json',
            },
            credentials: 'include'
        });

        if (response.status === 200) {
            const stringWithQuotes = await response.text();
            const requestId = stringWithQuotes.replace(/^"(.*)"$/, '$1');
            console.log(requestId)
            resolve(requestId);
        }
        else {
            reject("Błąd wysyłania żądania");
        }
    });
}

const get = (endpoint: string, jwt: string | null) : Promise<string> => {
    return new Promise( async ( resolve, reject ) => {
        if (jwt == null || jwt == '') {
            reject("Błąd wysyłania żądania");
            return;
        }
        const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
                "Authorization": "Bearer " + jwt,
                'Content-Type': 'application/json',
            },
            credentials: 'include'
        });

        if (response.status === 200) {
            const stringWithQuotes = await response.text();
            const requestId = stringWithQuotes.replace(/^"(.*)"$/, '$1');
            console.log(requestId)
            resolve(requestId);
        }
        else {
            reject("Błąd wysyłania żądania");
        }
    });
}

const googleLogin = (endpoint: string, code: string) : Promise<string> => {
    return new Promise( async ( resolve, reject ) => {
        console.log(endpoint, code);
        try {
            const response = await fetch(`${endpoint}?code=${code}&scope=''`, {
                method: 'GET',
                credentials: 'include'
            });

            if (response.status === 200) {
                const stringWithQuotes = await response.text();
                resolve(stringWithQuotes);
            }
        }
        catch (e) {
            reject(`Błąd wysyłania żądania: ${e}`);
        }
    });
}

const post = (endpoint: string, payload: object, jwt: string | null = null) : Promise<string> => {
    return new Promise( async ( resolve, reject ) => {
        console.log(endpoint, payload, JSON.stringify(payload));
        if (jwt == null || jwt == '') {
            reject("Błąd wysyłania żądania");
            return;
        }
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                "Authorization": "Bearer  " + jwt,
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        if (response.status === 200) {
            const stringWithQuotes = await response.text();
            const requestId = stringWithQuotes.replace(/^"(.*)"$/, '$1');
            console.log(requestId)
            resolve(requestId);
        }
        else {
            reject("Błąd wysyłania żądania");
        }
    });
}

const put = (endpoint: string, payload: object | null, jwt: string | null = null) : Promise<string> => {
    return new Promise( async ( resolve, reject ) => {
        console.log(endpoint, payload, JSON.stringify(payload));
        if (jwt == null || jwt == '') {
            reject("Błąd wysyłania żądania");
            return;
        }
        const response = await fetch(endpoint, {
            method: 'PUT',
            headers: {
                "Authorization": "Bearer  " + jwt,
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        if (response.status === 200) {
            const stringWithQuotes = await response.text();
            const requestId = stringWithQuotes.replace(/^"(.*)"$/, '$1');
            console.log(requestId)
            resolve(requestId);
        }
        else {
            reject("Błąd wysyłania żądania");
        }
    });
}

const pool = (requestId: string, jwt: string | null = null) : Promise<object> => {
    
    return new Promise( async ( resolve, reject ) => {
        if (jwt == null || jwt == '') {
            reject("Błąd wysyłania żądania");
            return;
        }
        const endpoint = `${API_POOL_ENDPOINT}/${requestId}`;

        const interval = setInterval(async () => {
            console.log(endpoint);
            let response = await fetch(endpoint, {
                headers: {
                    "Authorization": "Bearer  " + jwt
                },
                credentials: 'include'
            });

            if (response.status === 200) {
                clearInterval(interval);
                const backendResponse : BackendResponse = await response.json();
                console.log("BackendResponse", backendResponse);
                return (backendResponse.success)
                 ? resolve(backendResponse.payload)
                 : reject(backendResponse.error);
            }
            else if (response.status === 204) {
                // continue
            }
            else {
                clearInterval(interval);
                return reject("Coś poszło nie tak...");
            }

        }, API_POOL_INTERVAL);

        setTimeout(() => {
            clearInterval(interval);
            return reject("Przekroczono czas oczekiwania na odpowiedź")
        }, API_POOL_TIMEOUT);

    });
}